@if (open) {
  <div class="modal-backdrop" (click)="onClose()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>{{ 'message.create.title' | translate }}</h2>
        <button class="btn-icon" type="button" (click)="onClose()">
          <span class="material-icons">close</span>
        </button>
      </div>

      <form class="modal-body" [formGroup]="form">
        <div class="email-form">
          <div class="email-row">
            <ecab-input class="full-width" formControlName="subject" [label]="'message.subject' | translate"></ecab-input>
          </div>

          <div class="email-row options-row">
            <ecab-select class="full-width" formControlName="direction" [label]="'message.direction' | translate" [options]="getDirectionOptions()"></ecab-select>
            <ecab-select class="full-width" formControlName="channel" [label]="'message.channel' | translate" [options]="getChannelOptions()"></ecab-select>
            <ecab-select class="full-width" formControlName="language" [label]="'message.create.language' | translate" [options]="getLanguageOptions()"></ecab-select>
          </div>

          <div class="full-width content-field">
            <label class="content-label">{{ 'message.content' | translate }}</label>
            <app-markdown-editor formControlName="content"
                                 [placeholder]="'message.create.contentHint' | translate"
                                 [minHeight]="'220px'"></app-markdown-editor>
          </div>

          <div class="attachments-section">
            <div class="attachments-header">
              <div>
                <p class="attachments-title">{{ 'message.attachments.title' | translate }}</p>
                <p class="attachments-hint">{{ 'message.attachments.hint' | translate }}</p>
              </div>
              <label class="attachments-picker">
                <input class="attachments-input"
                       type="file"
                       multiple
                       (change)="onAttachmentsSelected($event)">
                <span>{{ 'message.attachments.add' | translate }}</span>
              </label>
            </div>

            @if (selectedAttachments().length > 0) {
              <div class="attachments-list">
                @for (attachment of selectedAttachments(); track attachment.id) {
                  <div class="attachment-item">
                    <div class="attachment-main">
                      <span class="material-icons attachment-icon">attach_file</span>
                      <div class="attachment-copy">
                        <span class="attachment-name">{{ attachment.filename }}</span>
                        <span class="attachment-meta">{{ getAttachmentTypeLabel(attachment) }} • {{ formatAttachmentSize(attachment.size) }}</span>
                      </div>
                    </div>

                    <button class="attachment-remove"
                            type="button"
                            [attr.aria-label]="'message.attachments.remove' | translate"
                            (click)="removeAttachment(attachment.id)">
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                }
              </div>
            } @else {
              <p class="attachments-empty">{{ 'message.attachments.empty' | translate }}</p>
            }
          </div>
        </div>

        <div class="qualification-section">
          <h3>{{ 'message.create.qualification' | translate }}</h3>

          <div class="qualification-columns">
            <div class="qualification-column">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>{{ 'message.create.label.person' | translate }}</mat-label>
                <input matInput
                       [matAutocomplete]="autoPerson"
                       [placeholder]="'message.context.searchPerson' | translate"
                       [ngModel]="personSearchTerm()"
                       [ngModelOptions]="{ standalone: true }"
                       (ngModelChange)="onPersonSearchChange($event)">
                @if (personSearchTerm()) {
                  <button matSuffix mat-icon-button type="button" (click)="clearPerson(); $event.stopPropagation()">
                    <span class="material-icons clear-icon">close</span>
                  </button>
                }
                <mat-autocomplete #autoPerson="matAutocomplete"
                                  [displayWith]="displayPerson"
                                  (optionSelected)="onPersonSelected($event.option.value)">
                  @for (option of personsOptions(); track option.id) {
                    <mat-option [value]="option">
                      <div class="person-option-row">
                        <span class="person-name">{{ getPersonPrimaryLabel(option) }}</span>
                        <div class="person-meta">
                          @if (option.birthDate) {
                            <span>{{ option.birthDate | date:'dd.MM.yyyy' }}</span>
                          }
                          @if (getPersonLocality(option)) {
                            <span class="meta-separator">-</span>
                            <span>{{ getPersonLocality(option) }}</span>
                          }
                        </div>
                      </div>
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>

              <ecab-select [ngModel]="selectedBuilding()"
                          [ngModelOptions]="{ standalone: true }"
                          (selectionChange)="onBuildingSelectChange($event)"
                          [label]="'message.create.label.building' | translate"
                          [options]="getBuildingOptions()">
              </ecab-select>

              <ecab-select [ngModel]="selectedCase()"
                          [ngModelOptions]="{ standalone: true }"
                          (selectionChange)="onCaseSelectChange($event)"
                          [label]="'message.create.label.case' | translate"
                          [options]="getCaseOptions()">
              </ecab-select>

              <ecab-select class="full-width" formControlName="questionType" [label]="'filter.category' | translate" [options]="getQuestionTypeOptions()"></ecab-select>
            </div>

            <div class="qualification-column">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>{{ 'message.create.label.collaborator' | translate }}</mat-label>
                <input matInput
                       [matAutocomplete]="autoCollaborator"
                       [placeholder]="'message.context.searchCollaborator' | translate"
                       [value]="collaboratorSearchTerm()"
                       (input)="collaboratorSearchTerm.set($event.target.value)">
                @if (collaboratorSearchTerm()) {
                  <button matSuffix mat-icon-button type="button" (click)="clearCollaborator(); $event.stopPropagation()">
                    <span class="material-icons clear-icon">close</span>
                  </button>
                }
                <mat-autocomplete #autoCollaborator="matAutocomplete"
                                  [displayWith]="displayCollaborator"
                                  (optionSelected)="onCollaboratorSelected($event.option.value)">
                  @for (option of filteredCollaboratorOptions(); track option.id) {
                    <mat-option [value]="option">
                      <div class="collaborator-option-row">
                        <span class="collaborator-option-name">{{ option.firstname }} {{ option.lastname }}</span>
                        <span class="collaborator-option-email">{{ option.email }}</span>
                      </div>
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            </div>
          </div>
        </div>
      </form>

      <div class="modal-footer">
        <ecab-button class="btn-secondary" variant="secondary" [label]="'message.context.cancel' | translate" (clicked)="onClose()"></ecab-button>
        <ecab-button class="btn-primary" variant="primary" [label]="'message.create.submit' | translate" [disabled]="submitting()" (clicked)="submit()"></ecab-button>
      </div>
    </div>
  </div>
}
